// src/pages/admin/Codes.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Edit,
  Delete,
  Add,
  Autorenew,
  ToggleOn,
  ToggleOff,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import {
  fetchCodes,
  createCode,
  generateCode,
  updateCode,
  deleteCode,
  toggleCode,
} from "../utils/api"; // adjust path if needed

export default function CodesPage() {
  const [codes, setCodes] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [editCode, setEditCode] = useState(null);
  const [newCode, setNewCode] = useState({
    type: "secureKey",
    code: "",
    is_active: true,
  });
  const [modalOpen, setModalOpen] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // Loading states
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Fetch codes on mount
  useEffect(() => {
    loadCodes();
  }, []);

  const loadCodes = async () => {
    try {
      const res = await fetchCodes();
      setCodes(res.data.data || res.data || []);
      setSnackbar({
        open: true,
        message: "Codes loaded successfully",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to load codes",
        severity: "error",
      });
    }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const res = await updateCode(editCode.id, editCode);
      setCodes((prev) =>
        prev.map((c) => (c.id === editCode.id ? res.data.code : c))
      );
      setEditCode(null);
      setSnackbar({
        open: true,
        message: "Code updated successfully",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({ open: true, message: "Update failed", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async () => {
    setAdding(true);
    try {
      const res = await createCode(newCode);
      setCodes((prev) => [...prev, res.data.code]);
      setNewCode({ type: "secureKey", code: "", is_active: true });
      setModalOpen(false);
      setSnackbar({
        open: true,
        message: "New code added successfully",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to add code",
        severity: "error",
      });
    } finally {
      setAdding(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await generateCode({ type: newCode.type });
      setCodes((prev) => [...prev, res.data.code]);
      setModalOpen(false);
      setSnackbar({
        open: true,
        message: "Code auto-generated successfully",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to generate code",
        severity: "error",
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      const res = await toggleCode(id);
      setCodes((prev) => prev.map((c) => (c.id === id ? res.data.code : c)));
      setSnackbar({
        open: true,
        message: "Code status toggled",
        severity: "info",
      });
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to toggle", severity: "error" });
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteCode(id);
      setCodes((prev) => prev.filter((c) => c.id !== id));
      setSnackbar({
        open: true,
        message: "Code deleted successfully",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to delete code",
        severity: "error",
      });
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5" sx={{ color: "#0ff", fontWeight: "bold" }}>
          Codes Management
        </Typography>
        <Button
          startIcon={<Add />}
          variant="contained"
          color="primary"
          onClick={() => setModalOpen(true)}
          sx={{
            transition: "all 0.2s ease",
            "&:hover": { backgroundColor: "#0ff", color: "#000" },
          }}
        >
          Add / Generate Code
        </Button>
      </Box>

      {/* Table */}
      <Card
        sx={{
          bgcolor: "rgba(25,25,50,0.7)",
          border: "1px solid #0ff4",
          borderRadius: 3,
          backdropFilter: "blur(12px)",
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(codes?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) || []).map(
                (code, index) => (
                  <motion.tr
                    key={code.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <TableCell>
                      <Chip
                        label={code.type}
                        sx={{
                          bgcolor: "rgba(0,255,255,0.2)",
                          color: "#0ff",
                          fontWeight: "bold",
                          boxShadow: "0 0 6px cyan",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {editCode?.id === code.id ? (
                        <TextField
                          size="small"
                          value={editCode.code}
                          onChange={(e) =>
                            setEditCode({
                              ...editCode,
                              code: e.target.value,
                            })
                          }
                          sx={{ input: { color: "#fff" } }}
                        />
                      ) : (
                        <Typography>{code.code}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={code.is_active ? "Active" : "Inactive"}
                        color={code.is_active ? "success" : "default"}
                        icon={code.is_active ? <ToggleOn /> : <ToggleOff />}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {editCode?.id === code.id ? (
                        <Button
                          variant="contained"
                          color="success"
                          onClick={handleSaveEdit}
                          disabled={saving}
                          startIcon={saving ? <CircularProgress size={18} /> : null}
                          sx={{ "&:hover": { opacity: 0.8 } }}
                        >
                          Save
                        </Button>
                      ) : (
                        <>
                          <Tooltip title="Edit">
                            <IconButton
                              color="info"
                              onClick={() => setEditCode(code)}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Toggle Active">
                            <IconButton
                              color="warning"
                              onClick={() => handleToggle(code.id)}
                            >
                              {code.is_active ? <ToggleOff /> : <ToggleOn />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              color="error"
                              onClick={() => handleDelete(code.id)}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </motion.tr>
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={codes?.length || 0}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Card>

      {/* Add/Generate Modal */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        TransitionProps={{ timeout: 0 }}
      >
        <DialogTitle>Create or Generate Code</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={newCode.type}
              onChange={(e) => setNewCode({ ...newCode, type: e.target.value })}
              label="Type"
            >
              <MenuItem value="secureKey">Secure Key</MenuItem>
              <MenuItem value="otpCode">OTP Code</MenuItem>
              <MenuItem value="tacCode">TAC Code</MenuItem>
              <MenuItem value="digitalCvv">Digital CVV</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Code (leave empty if auto-generate)"
            value={newCode.code}
            onChange={(e) => setNewCode({ ...newCode, code: e.target.value })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} color="error">
            Cancel
          </Button>
          <Button
            onClick={newCode.code ? handleAdd : handleGenerate}
            variant="contained"
            disabled={adding || generating}
            color={newCode.code ? "success" : "info"}
            startIcon={
              adding || generating ? (
                <CircularProgress size={18} />
              ) : newCode.code ? (
                <Add />
              ) : (
                <Autorenew />
              )
            }
            sx={{ "&:hover": { opacity: 0.85 } }}
          >
            {newCode.code ? "Add Code" : "Auto Generate"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}